import React from 'react';

import { FCWithChildren } from '@wix/challenges-web-library';
import utils, {
  ChallengeEntityType,
  getNextButtonLabel,
  isStepAvailableForComplete,
  isStepOverdue,
  isStepResolved,
} from '../../views/utils';
import { useResolveStep } from '../../../../../contexts/ResolveStep/ResolveStepContext';
import { useSettings } from '@wix/tpa-settings/react';
import { useMonitoring } from '../../../../../contexts/Monitoring/MonitoringContext';
import { useSections } from '../../../../../contexts/ParticipantSections/ParticipantSectionsContext';
import { useLoading } from '../../../../../contexts/Loading/LoadingContext';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { ButtonSize as ButtonSIZE } from 'wix-ui-tpa/cssVars';
import { useStepIsUnavailable } from './utils/useStepIsUnavailable';
import { useSidebarLayoutBase } from '../../views/SidebarLayout/contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { useVideoStepData } from '../../../../../hooks/useVideoStepData';
import { isUserCompleted } from '../../../../../contexts/User/helpers/userTypeHandlers';
import { useUser } from '../../../../../contexts/User/UserContext';
import TextWithDirection from '../../../../../components-shared/TextWithDirection';
import { PreviewActionBlockerContainer } from '../../../../../components-shared/ActionBlocker/PreviewActionBlockerContainer';
import { ActionBlockerAppearance } from '../../../../../components-shared/ActionBlocker/ActionBlocker';
import { SidebarLayoutButton } from '../Buttons/SidebarLayoutButton/SidebarLayoutButton';
import { StepsNavigation } from './components/StepsNavigation';
import { Box } from '../../../../../components-shared/Box';
import { StepUndo } from './components/StepUndo';
import challengeSettings from '../../../settingsParams';
import { IControl } from './utils/types';
import { FedopsInteraction } from '../../../../../config/fedopsInteraction';
import { Challenges } from '../../../../../editor/types/Experiments';
import {
  FocusPoint,
  useFocusManager,
} from '../../../../../contexts/FocusManager';
import { ParticipantStep } from '../../../../../types/v3Types';
import { useCSSPBEnabled } from '../../../../../hooks/useCSSPBStyle';
import { classes, st } from './StepControls.st.css';

export interface IStepControls extends IControl {
  completeBtnDataHook?: string;
  nextBtnDataHook?: string;
  currentStep: ParticipantStep;
  buttonState: string;
  onStepResolve(currentStep: ParticipantStep): Promise<void>;
}

export const StepControls: FCWithChildren<IStepControls> = (props) => {
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const settings = useSettings();
  const { participant } = useUser();
  const { isResolveStepRequestInProgress } = useResolveStep();
  const { startInteraction } = useMonitoring();
  const stepId = props.currentStep?.id;
  const { experiments } = useExperiments();
  const isStepUndoEnabled = experiments.enabled(Challenges.enableUndoButton);
  const isCompletedChallenge = isUserCompleted(
    participant?.transitions?.[0]?.state,
  );
  const cssPBEnabled = useCSSPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });

  const { isStepUnavailable, isSectionLocked } = useStepIsUnavailable();
  const { stepDescriptionLoaded } = useSections();
  const { hasRicosLoaded } = useLoading();
  const { setNextFocusPoint, focusPoint } = useFocusManager();

  const nextEntity = utils.getFollowingEntity(props.currentStep?.id, {
    listParticipantSections: props.sections,
    participantSteps: props.steps,
  });

  const isCompleteButtonShown =
    !isStepUnavailable &&
    !isCompletedChallenge &&
    !isSectionLocked &&
    (isStepAvailableForComplete(props.currentStep) ||
      isStepOverdue(props.currentStep));

  const isUndoButtonShown = !isCompletedChallenge && !isStepUnavailable;

  const stepResolved = isStepResolved(props.currentStep);
  const isGeneralNextButtonShown =
    stepResolved && nextEntity.type !== ChallengeEntityType.VOID;

  const { isVideo } = useVideoStepData(props.currentStep?.source);
  const { videoStepAccepted, isVideoLoadingError } = useSidebarLayoutBase();
  const [isVideoNotificationVisible, setIsVideoNotificationVisible] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    setIsVideoNotificationVisible(false);
  }, [stepId, videoStepAccepted]);

  const shouldHideButton =
    isMobile && (!hasRicosLoaded || !stepDescriptionLoaded(stepId));

  const CompleteButton =
    isCompleteButtonShown && !shouldHideButton ? (
      <PreviewActionBlockerContainer
        appearance={ActionBlockerAppearance.PopoverAppearance}
        content={t('action-blocker.preview-mode.complete-step')}
        isActionAllowed={false}
      >
        <SidebarLayoutButton
          className={classes.stepViewMainCTA}
          dataHook={props.completeBtnDataHook || null}
          disabled={isResolveStepRequestInProgress || isVideoLoadingError}
          fullWidth={isMobile}
          size={isMobile ? ButtonSIZE.small : ButtonSIZE.medium}
          onClick={async () => {
            setNextFocusPoint(FocusPoint.Bottom);
            if (isVideo && !videoStepAccepted) {
              setIsVideoNotificationVisible(true);
              return;
            }

            if (!isResolveStepRequestInProgress) {
              startInteraction(
                FedopsInteraction.CompleteStepWithoutFeedback,
                props.currentStep?.id,
              );

              await props.onStepResolve(props.currentStep);
            }
          }}
        >
          {settings.get(challengeSettings.completeButtonTxt) as string}
        </SidebarLayoutButton>
      </PreviewActionBlockerContainer>
    ) : null;

  const NextButton =
    isGeneralNextButtonShown && !shouldHideButton ? (
      <SidebarLayoutButton
        className={classes.stepViewMainCTA}
        key={stepId}
        dataHook={props.nextBtnDataHook || null}
        disabled={isResolveStepRequestInProgress}
        fullWidth={isMobile}
        size={isMobile ? ButtonSIZE.small : ButtonSIZE.medium}
        onClick={async () => {
          setNextFocusPoint(FocusPoint.Top);
          await props.onNextEntity(nextEntity);
        }}
        moveFocus={focusPoint === FocusPoint.Bottom}
      >
        {getNextButtonLabel({
          entity: nextEntity.type,
          t,
          nextButtonTxt: settings.get(
            challengeSettings.nextButtonTxt,
          ) as string,
        })}
      </SidebarLayoutButton>
    ) : null;

  const UndoButton =
    isUndoButtonShown && !shouldHideButton ? (
      <PreviewActionBlockerContainer
        appearance={ActionBlockerAppearance.PopoverAppearance}
        content={t('action-blocker.preview-mode.complete-step')}
        isActionAllowed={false}
      >
        <StepUndo className={classes.undo}>
          {settings.get(challengeSettings.undoButtonTxt) as string}
        </StepUndo>
      </PreviewActionBlockerContainer>
    ) : null;

  return (
    <div
      className={st(classes.root, {
        mobile: isMobile,
        align: !cssPBEnabled
          ? settings.get(challengeSettings.sidebarLayoutTextAlignment)
          : null,
        withCSSAlignment: cssPBEnabled,
        rtl: isRTL,
        buttonState: props.buttonState,
        stepResolved,
      })}
      role="region"
      aria-live="polite"
    >
      {!isStepUndoEnabled ? (
        <>
          {stepResolved ? (
            <>
              {isMobile ? (
                <>
                  <p className={classes.textMessage}>
                    <TextWithDirection>
                      {t('challenge.page.step.completed')}
                    </TextWithDirection>
                  </p>
                  {NextButton}
                </>
              ) : (
                <>
                  <p className={classes.textMessage}>
                    <TextWithDirection>
                      {t('challenge.page.step.completed')}
                    </TextWithDirection>
                  </p>
                  <Box gap={6} className={classes.boxNextButton}>
                    {NextButton}
                  </Box>
                </>
              )}
            </>
          ) : null}
          {CompleteButton}
          {isVideoNotificationVisible ? (
            <p className={classes.textMessage}>
              <TextWithDirection>
                {t('challenge.page.step-video-required.warning')}
              </TextWithDirection>
            </p>
          ) : null}
        </>
      ) : (
        <>
          {stepResolved ? (
            <p className={classes.textMessage}>
              <TextWithDirection>
                {t('challenge.page.step.completed')}
              </TextWithDirection>
            </p>
          ) : null}

          {stepResolved ? UndoButton : CompleteButton}

          {isVideoNotificationVisible ? (
            <p className={classes.textMessage}>
              <TextWithDirection>
                {t('challenge.page.step-video-required.warning')}
              </TextWithDirection>
            </p>
          ) : null}

          <StepsNavigation
            stepId={props.currentStep?.id}
            onChangeEntity={props.onNextEntity}
          />
        </>
      )}
    </div>
  );
};
